import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { SubmitErrorHandler, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { ModalBody } from 'Components/Modal/ModalBody'
import { FormTextInput } from 'Components/Inputs/FormTextInput'
import { FormAmountInput } from 'Components/Inputs/FormAmountInput'
import { FormDatePicker } from 'Components/Inputs/Calendar/FormDatePicker'
import { ControlledStyledSelect } from 'Components/Inputs/ControlledStyledSelect'
import { FileType, FileUpload } from 'Components/Inputs/FileUpload'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { StyledInputLabel } from 'Components/Inputs/StyledInputLabel'
import { Checkbox } from 'Components/Inputs/Checkbox/Checkbox'

import { useEmployerProgramsContext } from 'Contexts/EmployerProgramsContext'
import { useAddPurchaseForUserMutation } from 'Hooks/api/JOONAdmin/useAddPurchaseForUserMutation'
import { useOngoingPurchaseInfo } from 'Hooks/useOngoingPurchaseInfo'

import { ButtonContainer, FlexColumn, GridRow, spacingMD, spacingXXS } from 'Utils/styles/spacing'
import { Subtext } from 'Utils/styles/text'
import { createDate, displayDate, formatDateForServer } from 'Utils/helpers/dateUtils'
import { periodStartForDuration } from 'Utils/helpers/programUsage'
import { CurrencyCode, CurrencySymbol } from 'Utils/types/currencies'
import { UserDetailsForEmployer } from 'Utils/types/user'
import { isAmount, numToCurrency, parseAmount } from 'Utils/helpers/currency'
import { fromPairs } from 'ramda'

const InputContainer = styled(GridRow)`
  grid-gap: ${spacingXXS};
`

const Hint = styled.span`
  ${Subtext}
`

type AddPurchaseForUserFormValues = {
  name: string
  amount: string
  benefitProgramId: string
  benefitProgramCategoryId: string
  description: string
}

type AddPurchaseForUserFormProps = {
  user?: UserDetailsForEmployer
}
export const AddPurchaseForUserForm: FC<AddPurchaseForUserFormProps> = ({ user }) => {
  const {
    state: { allPrograms }
  } = useEmployerProgramsContext()

  const currencyCode = user?.currencyCode ?? CurrencyCode.USDollar
  const currencySymbol = CurrencySymbol[currencyCode]

  const userMembershipByProgramId = useMemo(() => {
    const map = new Map<number, { startDate: Date; endDate?: Date; expired: boolean }>()
    if (!user) return map
    user.activeMemberships?.forEach((m) =>
      map.set(m.programId, {
        startDate: m.membershipStartDate,
        endDate: m.membershipEndDate,
        expired: false
      })
    )
    user.futureMemberships?.forEach((m) =>
      map.set(m.programId, {
        startDate: m.membershipStartDate,
        endDate: m.membershipEndDate,
        expired: false
      })
    )
    user.expiredMemberships?.forEach((m) =>
      map.set(m.programId, {
        startDate: m.membershipStartDate,
        endDate: m.membershipEndDate,
        expired: true
      })
    )
    return map
  }, [user])

  const userPrograms = useMemo(
    () => allPrograms.filter((p) => userMembershipByProgramId.has(p.id)),
    [allPrograms, userMembershipByProgramId]
  )

  const endDateByProgramId = useMemo(() => {
    const map = new Map<number, Date>()
    userPrograms.forEach((p) => {
      const m = userMembershipByProgramId.get(p.id)
      const candidates: Date[] = []
      if (m?.endDate) candidates.push(m.endDate)
      if (p.policy.scheduledEndDate) candidates.push(p.policy.scheduledEndDate)
      if (candidates.length === 0) return
      const earliest = candidates.reduce((a, b) => (a < b ? a : b))
      map.set(p.id, earliest)
    })
    return map
  }, [userPrograms, userMembershipByProgramId])

  const programOptions = useMemo(() => {
    const today = new Date()
    return userPrograms.map((p) => {
      const endDate = endDateByProgramId.get(p.id)
      const isPast = endDate && endDate < today
      const label = isPast && endDate ? `${p.name} (Ended ${displayDate(endDate)})` : p.name
      return { value: p.id.toString(), label }
    })
  }, [userPrograms, endDateByProgramId])

  const categoryMap = useMemo(
    () =>
      fromPairs(
        userPrograms.map(({ id, categories }) => [
          `${id}`,
          categories.map((c) => ({ value: c.id.toString(), label: c.name }))
        ])
      ) as Record<string, { value: string; label: string }[]>,
    [userPrograms]
  )

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<AddPurchaseForUserFormValues>({
    defaultValues: {
      benefitProgramId: programOptions.length === 1 ? programOptions[0].value : ''
    }
  })

  const selectedProgramId = useWatch({ control, name: 'benefitProgramId' })
  const watchedName = useWatch({ control, name: 'name' })
  const watchedAmount = useWatch({ control, name: 'amount' })
  const watchedCategory = useWatch({ control, name: 'benefitProgramCategoryId' })

  const categoryOptions = useMemo(
    () => (selectedProgramId ? categoryMap[selectedProgramId] ?? [] : []),
    [categoryMap, selectedProgramId]
  )

  useEffect(() => {
    setValue('benefitProgramId', programOptions.length === 1 ? programOptions[0].value : '')
  }, [programOptions, setValue])

  useEffect(() => {
    setValue(
      'benefitProgramCategoryId',
      categoryOptions.length === 1 ? categoryOptions[0].value : ''
    )
  }, [categoryOptions, setValue])

  const [date, setDate] = useState<Date | undefined>(undefined)
  const updateSelectedDate = useCallback((newDate: Date) => setDate(newDate), [])

  const [ongoingPurchase, setOngoingPurchase] = useState(false)

  const [prevProgramId, setPrevProgramId] = useState(selectedProgramId)
  if (selectedProgramId !== prevProgramId) {
    setPrevProgramId(selectedProgramId)
    setDate(undefined)
    setOngoingPurchase(false)
  }

  const selectedProgram = useMemo(
    () => userPrograms.find((p) => p.id === Number(selectedProgramId)),
    [userPrograms, selectedProgramId]
  )

  const selectedMembership = useMemo(() => {
    if (!selectedProgramId) return undefined
    return userMembershipByProgramId.get(Number(selectedProgramId))
  }, [selectedProgramId, userMembershipByProgramId])

  const datePickerMinDate = useMemo(() => {
    const membershipStart = selectedMembership?.startDate
    const policyEnd = selectedProgram?.policy.scheduledEndDate

    if (selectedProgram && policyEnd && policyEnd < new Date()) {
      const periodStart = periodStartForDuration(
        policyEnd,
        selectedProgram.policy.policyPeriodDuration,
        selectedProgram.policy.annualResetMonth
      )
      return membershipStart && membershipStart > periodStart ? membershipStart : periodStart
    }
    return membershipStart
  }, [selectedProgram, selectedMembership])

  const datePickerMaxDate = useMemo(() => {
    const today = createDate()
    const programEnd = selectedProgram ? endDateByProgramId.get(selectedProgram.id) : undefined
    return programEnd && programEnd < today ? programEnd : today
  }, [selectedProgram, endDateByProgramId])

  const offersOpr = useMemo(() => {
    if (!selectedProgram?.policy.ongoingPurchases) return false

    const endDate = endDateByProgramId.get(selectedProgram.id)
    const hasEnded = !!endDate && endDate < new Date()
    return !hasEnded
  }, [selectedProgram, endDateByProgramId])

  const [file, setFile] = useState<File | null>(null)
  const [autoApprove, setAutoApprove] = useState(true)

  const addPurchaseMutation = useAddPurchaseForUserMutation()

  const registerOptions = {
    name: { required: 'Merchant/Name is required' },
    amount: {
      validate: (value: unknown) => isAmount(value) || 'A valid amount is required.'
    },
    selectedProgram: { required: 'Benefit program is required' },
    selectedCategory: { required: 'Benefit category is required' }
  }

  const purchaseAmount = useMemo(() => parseAmount(watchedAmount ?? ''), [watchedAmount])
  const hasValidAmount = purchaseAmount !== undefined

  const isFormComplete =
    !!watchedName && hasValidAmount && !!date && !!selectedProgramId && !!watchedCategory

  const { amountMeetsThreshold, purchaseAmountRequired, activeOngoingPurchaseForProgram } =
    useOngoingPurchaseInfo(
      user?.id,
      selectedProgram && {
        name: selectedProgram.name,
        ongoingPurchases: selectedProgram.policy.ongoingPurchases,
        allowance: selectedProgram.policy.expenditureAllowance,
        threshold: Number(selectedProgram.policy.ongoingPurchaseThreshold ?? 0)
      },
      purchaseAmount
    )

  const oprUnavailableReason = useMemo(() => {
    if (activeOngoingPurchaseForProgram) {
      return 'This user already has an active OPR in this program.'
    }
    if (!amountMeetsThreshold) {
      const minimum = numToCurrency(purchaseAmountRequired, currencyCode)
      return `Purchase must be at least ${minimum} for an OPR.`
    }
    return undefined
  }, [activeOngoingPurchaseForProgram, amountMeetsThreshold, purchaseAmountRequired, currencyCode])

  const setsUpOpr = ongoingPurchase && !oprUnavailableReason

  const handleSubmission: SubmitHandler<AddPurchaseForUserFormValues> = async (data) => {
    if (!date) {
      toast.error('A purchase date is required.')
      return
    }
    if (!user) {
      toast.error('No user selected.')
      return
    }

    const { name, amount, description, benefitProgramId, benefitProgramCategoryId } = data
    const formData = new FormData()

    formData.append('user_id', user.id.toString())
    formData.append('name', name)
    formData.append('amount', `${parseAmount(amount)}`)
    formData.append('benefit_program_id', benefitProgramId)
    formData.append('basic_benefit_program_category_id', benefitProgramCategoryId)
    formData.append('payment_meta', description ?? '')
    formData.append('transaction_date', formatDateForServer(date))
    formData.append('ongoing_purchase', setsUpOpr ? 'true' : 'false')
    formData.append('auto_approve', autoApprove ? 'true' : 'false')

    if (file) {
      formData.append('file0', file as Blob)
    }

    addPurchaseMutation.mutate({ formData })
  }

  const handleError: SubmitErrorHandler<AddPurchaseForUserFormValues> = (error) => {
    toast.error(
      error.name?.message ??
        error.amount?.message ??
        error.benefitProgramId?.message ??
        error.benefitProgramCategoryId?.message
    )
  }

  return (
    <form onSubmit={handleSubmit(handleSubmission, handleError)}>
      <ModalBody maxHeight="calc(80vh - 150px)">
        <FormTextInput
          fieldName="name"
          errorPresent={!!errors?.name?.message}
          labelId="merchant.label"
          register={register}
          registerOptions={registerOptions.name}
        />
        <FormAmountInput
          fieldName="amount"
          currencySymbol={currencySymbol}
          errorPresent={!!errors?.amount?.message}
          labelId="amount.label"
          register={register}
          registerOptions={registerOptions.amount}
        />
        <InputContainer>
          <StyledInputLabel>Purchase Date</StyledInputLabel>
          <FormDatePicker
            minDate={datePickerMinDate}
            maxDate={datePickerMaxDate}
            date={date}
            onChange={updateSelectedDate}
            disabled={!selectedProgramId}
          />
        </InputContainer>
        <ControlledStyledSelect
          control={control}
          name={'benefitProgramId'}
          label={'Benefit Program'}
          options={programOptions}
          errorPresent={!!errors?.benefitProgramId?.message}
          placeholder={'Select a program...'}
          rules={registerOptions.selectedProgram}
        />
        <ControlledStyledSelect
          control={control}
          name={'benefitProgramCategoryId'}
          label={'Benefit Category'}
          options={categoryOptions}
          errorPresent={!!errors?.benefitProgramCategoryId?.message}
          disabled={!selectedProgramId || categoryOptions.length < 2}
          defaultValue={categoryOptions.length === 1 ? categoryOptions[0].value : undefined}
          placeholder={'Select a category...'}
          rules={registerOptions.selectedCategory}
        />
        <FormTextInput
          fieldName="description"
          errorPresent={false}
          labelId="description.label"
          register={register}
          registerOptions={{}}
        />
        <FileUpload setFileValue={setFile} fileType={FileType.Images} />
        {offersOpr && (
          <FlexColumn gap={spacingXXS}>
            <Checkbox
              labelText="Set up an OPR"
              value={setsUpOpr}
              disabled={!!oprUnavailableReason}
              onClick={() => setOngoingPurchase(!ongoingPurchase)}
            />
            {oprUnavailableReason && <Hint>{oprUnavailableReason}</Hint>}
          </FlexColumn>
        )}
        <Checkbox
          labelText="Auto-approve purchase"
          value={autoApprove}
          onClick={() => setAutoApprove(!autoApprove)}
        />
      </ModalBody>
      <ButtonContainer marginTop={spacingMD}>
        <StyledButton
          primary
          id="addPurchaseForUser.label"
          isLoading={addPurchaseMutation.isLoading}
          disabled={!isFormComplete}
        />
      </ButtonContainer>
    </form>
  )
}
